<template>
  <v-container id="order-details" fluid>
    <v-card :disabled="loadingBtn" outlined>
      <v-tabs v-if="order" v-model="selectedTab" grow>
        <v-tab>{{ $t('order.order') }}</v-tab>
        <v-tab :disabled="order.sub_orders.length === 0">{{ $t('order.subOrders') }}</v-tab>
        <v-tab v-if="partnerSettings.returns_access && 'click_and_reserve' !== order.type" @click="listReturns">
          {{ $t('order.returns') }}
        </v-tab>
        <v-tab>{{ $t('order.paymentInformation') }}</v-tab>
        <v-tab>{{ $t('order.shipmentInfo') }}</v-tab>
        <v-tab :disabled="order.custom.length === 0">{{ $t('order.customFields') }}</v-tab>
        <v-tab @change="listTriggerHistories">{{ $t('trigger.triggers') }}</v-tab>
        <v-tab @click="listDocuments">{{ $t('order.documents.documents') }}</v-tab>
        <v-tab @click="listRefunds">{{ $t('order.refunds') }}</v-tab>
        <v-tab-item>
          <v-container fluid>
            <order-headers-mobile v-if="isMobile" :order="order" />
            <order-headers v-else :order="order" />
            <v-row>
              <v-col class="pb-0" md="12">
                <v-data-table
                  :footer-props="footerProps"
                  :headers="orderHeadersComputed"
                  :items="order.items"
                  :loading="loadingData"
                  :loading-text="$t('product.productsLoading')"
                  :no-data-text="$t('product.noProducts')"
                  :options.sync="options"
                  :server-items-length="order.items.length"
                  dense
                  hide-default-footer
                  item-key="id"
                  mobile-breakpoint="700"
                >
                  <template v-slot:item.product_title="{ item }">
                    <v-col v-if="isMobile" class="pr-0">{{ item.product_title }}</v-col>
                    <span v-else>{{ item.product_title }}</span>
                  </template>
                  <template v-slot:item.validated_quantity="{ item }">
                    <span>{{ item.validated_quantity ? item.validated_quantity : 0 }}</span>
                  </template>
                  <template v-slot:item.returned_quantity="{ item }">
                    <span>{{ item.returned_quantity || defaultReturnedQuantity }}</span>
                  </template>
                  <template v-slot:item.product_sale_price="{ item }">
                    <del v-if="item.product_default_price && item.product_default_price > item.product_sale_price">
                      {{ item.product_default_price | formatPrice(order.currency) }}
                      {{ order.currency ? order.currency.code : order.currency_code }}
                    </del>
                    <span>
                      {{ item.product_sale_price | formatPrice(order.currency) }}
                      {{ order.currency ? order.currency.code : order.currency_code }}
                    </span>
                  </template>
                  <template v-slot:item.current_status="{ item }">
                    <span>{{ getItemStatusTranslation(item.current_status) }}</span>
                  </template>
                  <template v-slot:item.product_ean="{ item }">
                    <span v-if="item.barcode || item.product_ean">
                      {{ item.product_ean || item.barcode | formatBarCode() }}
                    </span>
                  </template>
                  <template v-slot:item.barcode="{ item }">
                    <span v-if="item.barcode && !item.product_ean">{{ item.barcode | formatBarCode() }}</span>
                  </template>
                  <template v-slot:item.custom="{ item }">
                    <v-btn
                      :disabled="item.custom.length == 0"
                      color="primary"
                      icon
                      @click="openCustomItemFieldsDialog(item)"
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row v-if="!['cancelled', 'partially_delivered', 'delivered'].includes(order.current_status)">
              <v-col class="pt-0">
                <v-divider />
              </v-col>
            </v-row>
            <v-row v-if="order.current_status === 'to_pickup'" class="mb-1" justify="center">
              <v-col cols="12" class="pb-1" align="center">
                <v-btn
                  :disabled="loadingBtn"
                  :large="!isMobile"
                  :loading="loadingBtn"
                  :medium="isMobile"
                  class="v-btn-footer"
                  color="primary"
                  depressed
                  tile
                  @click="openCustomerDeliveryModalOrUpdate('delivered')"
                >
                  {{ $t('subOrder.PackageDeliveredToCustomer') }}
                </v-btn>
              </v-col>
              <v-col v-if="pickupCodeDisplayAllowed" cols="12" class="pt-1 pb-0" align="center">
                <v-btn :loading="loadingData" color="secondary" depressed tile @click="displayPickupCodeDialog">
                  {{ $t('order.seePickupCode') }}
                  <v-icon right dark>mdi-eye-outline</v-icon>
                </v-btn>
              </v-col>
              <v-col v-if="pickupCodeResendAllowed" cols="12" class="pt-1 pb-0" align="center">
                <v-btn
                  :disabled="loadingResendPickupCode"
                  :loading="loadingData || loadingResendPickupCode"
                  color="secondary"
                  depressed
                  tile
                  @click="resendProductsDialog = true"
                >
                  {{ $t('order.resendPickupCode') }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="displayModifyPickupDeadlineButton" class="mb-1">
              <v-col>
                <v-row justify="center">
                  <v-btn
                    class="v-btn-footer"
                    :large="!isMobile"
                    :medium="isMobile"
                    depressed
                    outlined
                    tile
                    :disabled="loadingBtn || maxExtensionDateReached"
                    :loading="loadingBtn"
                    @click="dateSelectionDialog = true"
                  >
                    {{ $t('order.modifyPickupDeadline') }}
                  </v-btn>
                </v-row>
              </v-col>
            </v-row>
            <v-row v-if="canForceDeliveredStatus" class="mb-1" justify="center">
              <v-btn
                :disabled="loadingBtn"
                :large="!isMobile"
                :loading="loadingBtn"
                :medium="isMobile"
                class="v-btn-footer"
                color="primary"
                depressed
                tile
                @click="forceDeliveredDialog = true"
              >
                {{ $t('order.forceDelivered') }}
              </v-btn>
            </v-row>
            <v-row v-if="allowRelaunch" class="mb-2" justify="center">
              <v-btn
                :dark="isDark"
                :disabled="loadingData"
                :large="!isMobile"
                :loading="loadingData"
                :medium="isMobile"
                class="v-btn-footer"
                color="primary"
                tile
                depressed
                @click="openOrderRelaunchDialog()"
              >
                {{ $t('order.relaunchOrder') }}
              </v-btn>
            </v-row>
            <v-row v-if="canValidatePayments" class="mb-2" justify="center">
              <v-btn
                :disabled="loadingPaymentValidation"
                :large="!isMobile"
                :loading="loadingPaymentValidation"
                :medium="isMobile"
                class="v-btn-footer"
                color="primary"
                depressed
                tile
                @click="paymentValidationDialog = true"
              >
                {{ $t('order.validatePaymentsBtn') }}
              </v-btn>
            </v-row>
            <v-row class="mb-2" justify="center">
              <v-btn
                v-if="!['cancelled', 'partially_delivered', 'delivered'].includes(order.current_status)"
                :dark="isDark"
                :disabled="cancellation"
                :large="!isMobile"
                :loading="cancellation"
                :medium="isMobile"
                class="v-btn-footer"
                color="secondary"
                outlined
                tile
                @click="cancelOrderDialog = true"
              >
                {{ $t('subOrder.cancelOrder') }}
              </v-btn>
            </v-row>
            <v-row v-if="displayCoherisButton" class="mb-1" justify="center">
              <v-btn
                :disabled="loadingBtn"
                :large="!isMobile"
                :loading="loadingBtn"
                :medium="isMobile"
                class="v-btn-footer"
                color=""
                outlined
                tile
                @click="openCoherisCase()"
              >
                {{ $t('order.openCase') }}
              </v-btn>
            </v-row>
            <customer-details-bar
              v-if="mustDisplayCustomerInformation"
              :billingAddress="order.billing_address"
              :customer="order.customer"
              :shippingAddress="order.shipping_address"
            ></customer-details-bar>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <v-container fluid>
            <v-data-table
              :headers="subOrderHeadersComputed"
              :items="order.sub_orders"
              :loading="loadingData"
              :loading-text="$t('product.productsLoading')"
              :no-data-text="$t('product.noProducts')"
              dense
              disable-pagination
              hide-default-footer
              item-key="id"
              mobile-breakpoint="700"
            >
              <template v-slot:item.type="{ item }">
                <span>{{ getTypeTranslation(item.type) }}</span>
              </template>
              <template v-slot:item.current_status="{ item }">
                <span @click="openHistoryDialog(item)">
                  <v-badge
                    :style="
                      $vuetify.breakpoint.width <= 768 && $vuetify.breakpoint.width >= 700 ? 'width: min-content' : ''
                    "
                    color="primary"
                    icon="mdi-history"
                    inline
                  >
                    {{ getStatusTranslation(item.current_status) }}
                  </v-badge>
                </span>
              </template>
              <template v-slot:item.products="{ item }">
                <span>{{ getNbProducts(item.items) }}</span>
              </template>
              <template v-slot:item.validated_products="{ item }">
                <span>{{ getNbProductsValidated(item.items) }}</span>
              </template>
              <template v-slot:item.status="{ item }">
                <v-btn
                  :class="[!isMobile ? '' : 'pr-0']"
                  :disabled="['cancelled', 'partially_delivered', 'delivered'].includes(item.current_status)"
                  color="error"
                  text
                  @click=";(cancelSubOrderDialog = true), (indexActionSuborder = getIndexOfSuborder(item))"
                >
                  {{ $t('subOrder.cancel') }}
                </v-btn>
                <cancel-sub-order-dialog
                  v-if="getIndexOfSuborder(item) == indexActionSuborder"
                  v-model="cancelSubOrderDialog"
                  :choices="cancelReasons"
                  :index="getIndexOfSuborder(item)"
                  :introductionSentence="$t('subOrder.cancelReasonText')"
                  :title="$t('subOrder.cancelReasonTitle')"
                  @confirm="cancelSubOrder"
                >
                  <template v-slot:admin-options>
                    <v-switch
                      v-model="reorchestrate"
                      :label="$t('order.reorchestrateSubOrder')"
                      class="mt-0 pt-0"
                      dense
                    ></v-switch>
                  </template>
                </cancel-sub-order-dialog>
              </template>

              <template v-slot:item.details="{ item, index }">
                <div class="d-flex align-center">
                  <v-btn class="pr-1" color="primary" text @click="viewDetails(item.order_id)">
                    <v-icon v-if="$vuetify.breakpoint.width > 650 && $vuetify.breakpoint.width < 1264">
                      mdi-magnify
                    </v-icon>
                    <span v-else>{{ $t('order.viewDetails') }}</span>
                  </v-btn>

                  <v-tooltip v-if="item.pick_pack_ship" v-model="showSubOrderInPPSTooltipFull[index]" top color="red">
                    <template v-slot:activator="{ attrs }">
                      <v-btn
                        small
                        dense
                        icon
                        v-bind="attrs"
                        color="red"
                        @click="togglePickPackShipTooltipFull(index)"
                        class="ml-0 pl-O"
                      >
                        <v-icon>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                    {{ getPickPackShipMessage(item.pick_pack_ship.picker, item.pick_pack_ship.reference) }}
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-container>
        </v-tab-item>
        <v-tab-item v-if="partnerSettings.returns_access && 'click_and_reserve' !== order.type" @click="listReturns">
          <v-container fluid>
            <v-data-table
              :headers="returnsHeaders"
              :items="returns"
              :loading="loadingData"
              :loading-text="$t('order.returnsLoading')"
              :no-data-text="$t('order.noReturns')"
              dense
              disable-pagination
              hide-default-footer
              item-key="id"
              mobile-breakpoint="768"
            >
              <template v-slot:item.current_status="{ item }">
                <span @click="openHistoryDialog(item)">
                  <v-badge color="primary" icon="mdi-history" inline>
                    {{ getStatusTranslation(item.current_status) }}
                  </v-badge>
                </span>
              </template>
              <template v-slot:item.returned_products="{ item }">
                <span>{{ getNbProductsReturned(item.items) }}</span>
              </template>
              <template v-slot:item.details="{ item }">
                <v-btn
                  :class="[!isMobile ? '' : 'pr-0']"
                  color="primary"
                  text
                  @click="viewReturnDetails(item.return_id)"
                >
                  <v-icon v-if="$vuetify.breakpoint.width > 767 && $vuetify.breakpoint.width < 1264">
                    mdi-magnify
                  </v-icon>
                  <span v-else>{{ $t('order.viewDetails') }}</span>
                </v-btn>
              </template>
              <template v-slot:footer>
                <v-divider />
                <v-row>
                  <v-col class="text-center mt-3">
                    <v-card-text v-if="orderAlreadyFullyReturned(order)">
                      {{ $t('order.allProductsReturned') }}
                    </v-card-text>
                    <v-btn
                      v-else
                      :disabled="
                        !partnerSettings.returns_access ||
                        !['delivered', 'partially_delivered'].includes(order.current_status)
                      "
                      class="v-btn-footer"
                      color="primary"
                      depressed
                      tile
                      @click="createReturn(order.order_id)"
                    >
                      {{ $t('order.createReturn') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </template>
            </v-data-table>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <v-container>
            <v-data-table
              :headers="paymentHeaders"
              :items="order.payments"
              disable-sort
              hide-default-footer
              item-key="id"
              mobile-breakpoint="700"
            >
              <template v-slot:item.payment_reference="{ item }">
                <span v-if="!item.payment_reference">X</span>
                <span v-else>{{ item.payment_reference }}</span>
              </template>
              <template v-slot:item.payment_amount="{ item }">
                <span v-if="!item.payment_amount">X</span>
                <span v-else>
                  {{ item.payment_amount | formatPrice(order.currency) }}
                  {{ order.currency ? order.currency.code : order.currency_code }}
                </span>
              </template>
              <template v-slot:item.erp_payment_method_id="{ item }">
                <span v-if="!item.erp_payment_method_id">X</span>
                <span v-else>{{ item.erp_payment_method_id }}</span>
              </template>
              <template v-slot:item.payment_method_id="{ item }">
                <span v-if="!item.payment_method_id">X</span>
                <span v-else>{{ item.payment_method_id }}</span>
              </template>
            </v-data-table>
            <v-row v-if="canValidatePayments" class="mt-2 mb-2" justify="center">
              <v-btn
                :disabled="loadingPaymentValidation"
                :large="!isMobile"
                :loading="loadingPaymentValidation"
                :medium="isMobile"
                class="v-btn-footer"
                color="primary"
                depressed
                tile
                @click="paymentValidationDialog = true"
              >
                {{ $t('order.validatePaymentsBtn') }}
              </v-btn>
            </v-row>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <v-container>
            <v-data-table
              :headers="shipmentHeaders"
              :items="subOrderShipments"
              disable-sort
              hide-default-footer
              item-key="id"
              mobile-breakpoint="700"
            >
              <template v-slot:item.external_reference="{ item }">
                <span v-if="!item.external_reference">X</span>
                <span v-else>{{ item.external_reference }}</span>
              </template>
              <template v-slot:item.tracking_reference="{ item }">
                <span v-if="!item.tracking_reference">X</span>
                <span v-else>
                  {{ item.tracking_reference }}
                  <v-icon
                    v-if="item.tracking_reference"
                    class="ml-1 aligned-icon"
                    small
                    @click="copyReference(item.tracking_reference)"
                  >
                    mdi-content-copy
                  </v-icon>
                </span>
              </template>
              <template v-slot:item.action="{ item }">
                <a
                  v-if="item.tracking_url"
                  :href="safeURL(item.tracking_url)"
                  class="not-underlined-link"
                  target="_blank"
                >
                  {{ $t('order.linkToTracking') }}
                  <v-icon class="aligned-icon" color="primary" small>mdi-open-in-new</v-icon>
                </a>
              </template>
            </v-data-table>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <v-container>
            <v-data-table
              :headers="customFieldsHeaders"
              :items="order.custom"
              disable-sort
              hide-default-footer
              item-key="id"
              mobile-breakpoint="768"
            >
              <template v-slot:item.value="{ item }">
                <v-textarea
                  v-if="typeof item.value == 'string' && item.value.length > 28"
                  :value="item.value"
                  auto-grow
                  hide-details
                  readonly
                />
                <span v-else>{{ item.value }}</span>
              </template>
            </v-data-table>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <v-container>
            <v-data-table
              :headers="triggerHeaders"
              :items="triggersHistoriesData"
              :items-per-page="-1"
              :loading="loadingData"
              :loading-text="$t('triggersHistories.triggersHistoriesLoading')"
              :no-data-text="$t('triggersHistories.noTriggersHistories')"
              dense
              disable-sort
              hide-default-footer
              item-key="_id"
              mobile-breakpoint="768"
              show-expand
            >
              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="getTriggerStatusBackgroundColor(item.status)"
                  :text-color="getTriggerStatusTextColor(item.status)"
                  small
                >
                  {{ getTriggersStatusTranslation(item.status) }}
                </v-chip>
              </template>
              <template v-slot:item.date="{ item }">
                <span>{{ new Date(item.date) | moment('DD/MM/YYYY HH:mm:ss') }}</span>
              </template>
              <template v-slot:item.action="{ item }">
                <v-btn
                  v-if="isAdmin"
                  :class="[$vuetify.breakpoint.width >= 481 ? '' : 'pr-0']"
                  color="primary"
                  text
                  @click="goToTriggerHistory(item._id)"
                >
                  <v-icon v-if="$vuetify.breakpoint.width > 767 && $vuetify.breakpoint.width < 1264">
                    mdi-magnify
                  </v-icon>
                  <span v-else>{{ $t('triggersHistories.details') }}</span>
                </v-btn>
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="px-8 py-2 data_table_header_color">
                  <trigger-calls-panel :calls="item.trigger.trigger_calls" />
                </td>
              </template>
            </v-data-table>
          </v-container>
        </v-tab-item>
        <v-tab-item>
          <div v-if="documentsLoadError" class="text-center py-12">
            <v-icon large color="error">mdi-alert-circle-outline</v-icon>
            <div class="mt-3">{{ $t('documents.loadingError') }}</div>
            <v-btn class="mt-3" color="primary" outlined @click="listDocuments">{{ $t('documents.retry') }}</v-btn>
          </div>
          <orders-documents-table v-else :documents="documents" :loading="loadingDocuments" />
        </v-tab-item>
        <v-tab-item>
          <div v-if="refundsLoadError" class="text-center py-12">
            <v-icon large color="error">mdi-alert-circle-outline</v-icon>
            <div class="mt-3">{{ $t('refund.refundsLoadingError') }}</div>
            <v-btn class="mt-3" color="primary" outlined @click="listRefunds">{{ $t('refund.retry') }}</v-btn>
          </div>
          <orders-refunds-table v-else :refunds="refunds" :loading="loadingData" />
        </v-tab-item>
      </v-tabs>
    </v-card>
    <order-status-history-dialog
      v-model="showHistoryDialog"
      :item="targetItem"
      @close="closeHistoryDialog()"
    ></order-status-history-dialog>
    <cancel-order-dialog
      v-model="cancelOrderDialog"
      :choices="cancelReasons"
      :introductionSentence="$t('subOrder.cancelReasonText')"
      :title="$t('subOrder.cancelReasonTitle')"
      @confirm="cancel"
    >
      <template v-slot:admin-options>
        <v-switch
          v-model="sendCancelledEmail"
          :label="$t('order.sendCancelledEmail')"
          class="mt-0 pt-0"
          dense
        ></v-switch>
      </template>
    </cancel-order-dialog>
    <ItemCustomFields
      v-if="customItems"
      v-model="showCustomItemFieldsDialog"
      :customs="customItems"
      @close="closeCustomFieldsDialog()"
    />
    <customer-delivery-dialog
      v-if="customerDeliveryDialog"
      v-model="customerDeliveryDialog"
      @validateCustomerDelivery="validateCustomerDelivery($event)"
    ></customer-delivery-dialog>
    <workflow-choice-dialog
      v-model="showRelaunchOrderDialog"
      :choices="workflows"
      :loading="loadingData"
      @confirm="relaunchOrder"
    ></workflow-choice-dialog>
    <payment-validation-dialog
      v-if="paymentValidationDialog"
      v-model="paymentValidationDialog"
      :order="order"
      :payment-methods="allowedPaymentMethods"
      :loading="loadingPaymentValidation"
      @validateForm="validatePayments"
      @close="paymentValidationDialog = false"
    />
    <force-delivered-dialog
      v-model="forceDeliveredDialog"
      :title="$t('order.forceDelivered')"
      :text="$t('order.forceDeliveredStatus')"
      @no="forceDeliveredDialog = false"
      @yes="validateForceDelivered"
    ></force-delivered-dialog>
    <date-selection-dialog
      v-if="dateSelectionDialog"
      v-model="dateSelectionDialog"
      :maxDate="maxExtensionDate"
      :title="$t('order.modifyPickupDeadline')"
      :introductionSentence="$t('order.modifyPickupDeadlineMessage')"
      :currentDeadline="pickupDeadline"
      :filterDateFunction="deadlinesAreValid"
      @close="dateSelectionDialog = false"
      @validate="onDeadlineChange"
    ></date-selection-dialog>
    <information-dialog
      v-model="showDisplayPickupCodeDialog"
      :title="$t('order.pickupCode')"
      :introductionSentence="displayCodeIntroSentence"
    >
      <template v-slot:content>
        <v-list-item class="pt-0 pb-2">
          <v-row>
            <v-col align="center" class="pt-0">
              <v-progress-circular v-if="loadingPickupCode" class="pb-8" indeterminate></v-progress-circular>
              <span v-else-if="pickupCodeError" class="red--text py-0">
                {{ $t('order.errorLoadingPickupCode') }}
              </span>
              <span v-else-if="pickupCode" class="py-0">{{ pickupCode }}</span>
            </v-col>
          </v-row>
        </v-list-item>
      </template>
    </information-dialog>
    <confirmation-dialog
      v-model="resendProductsDialog"
      :title="$t('order.resendPickupCodeDialog')"
      :text="$t('order.resendPickupCodeDialogMessage')"
      :loading="loadingResendPickupCode"
      @no="resendProductsDialog = false"
      @yes="resendPickupCode"
    ></confirmation-dialog>
  </v-container>
</template>

<script>
  import CustomerDetailsBar from '@/components/CustomerDetailsBar.vue'
  import CancelSubOrderDialog from '@/components/MultipleChoicesDialog.vue'
  import CustomerDeliveryDialog from '@/components/SubOrders/CustomerDeliveryDialog.vue'
  import ItemCustomFields from '@/components/dialogs/ItemCustomFields.vue'
  import workflowChoiceDialog from '@/components/dialogs/WorkflowChoiceDialog.vue'
  import OrderStatusHistoryDialog from '@/components/dialogs/StatusHistoryDialog.vue'
  import OrderHeaders from '@/components/orders/OrderHeaders.vue'
  import OrderHeadersMobile from '@/components/orders/OrderHeadersMobile.vue'
  import { mapState } from 'vuex'
  import { EventBus } from '@/event-bus'
  import confirmationDialog from '@/components/ConfirmationDialog.vue'
  import { sanitizeUrl } from '@braintree/sanitize-url'
  import dateSelectionDialog from '@/components/dialogs/DateSelectionDialog.vue'
  import InformationDialog from '@/components/dialogs/InformationDialog.vue'
  import {
    checkDeadlineIsValid,
    computeMaxExtensionDate,
    canDisplayPickupDeadlineButton,
    applyPickupLocationToComponent,
    updatePickupDeadline,
    maxExtensionDateReached,
  } from '../../helpers/pickupDeadlineHelper'
  import { OrderSettingsService } from '@/services/orderSettingsService'
  import { OrderService } from '@/services/orderService'
  import { RefundService } from '@/services/refundService'
  import { DocumentService } from '@/services/documentService'
  import OrdersDocumentsTable from '@/components/orders/OrdersDocumentsTable.vue'
  import OrdersRefundsTable from '@/components/orders/OrdersRefundsTable.vue'
  import PaymentValidationDialog from '@/components/dialogs/PaymentValidationDialog.vue'
  import TriggerCallsPanel from '@/components/triggersHistories/TriggerCallsPanel.vue'
  import { getTriggerStatusBackgroundColor, getTriggerStatusTextColor } from '@/helpers/helper'

  export default {
    name: 'orderDetails',
    components: {
      CustomerDetailsBar,
      CancelSubOrderDialog,
      OrderStatusHistoryDialog,
      ItemCustomFields,
      cancelOrderDialog: CancelSubOrderDialog,
      OrderHeadersMobile,
      OrderHeaders,
      CustomerDeliveryDialog,
      workflowChoiceDialog: workflowChoiceDialog,
      forceDeliveredDialog: confirmationDialog,
      dateSelectionDialog,
      InformationDialog,
      confirmationDialog,
      OrdersDocumentsTable,
      OrdersRefundsTable,
      PaymentValidationDialog,
      TriggerCallsPanel,
    },
    data() {
      return {
        defaultReturnedQuantity: 0,
        order: null,
        loadingData: false,
        loadingBtn: false,
        cancelSubOrderDialog: false,
        cancelOrderDialog: false,
        cancellation: false,
        reorchestrate: true,
        dialogCancelSubOrder: false,
        showHistoryDialog: false,
        targetItem: undefined,
        indexActionSuborder: undefined,
        showCustomItemFieldsDialog: false,
        customItems: null,
        options: {
          itemsPerPage: 15,
        },
        returnsLoaded: false,
        returns: [],
        refundsLoaded: false,
        refundsLoadError: false,
        refunds: [],
        refundService: null,
        documentService: null,
        documents: [],
        documentsLoaded: false,
        documentsLoadError: false,
        loadingDocuments: false,
        returnsHeaders: [
          { text: this.$t('order.reference'), value: 'return_id', sortable: false },
          { text: this.$t('order.status'), value: 'current_status', sortable: false },
          { text: this.$t('order.returnedProducts'), value: 'returned_products', sortable: false },
          { text: '', value: 'details', sortable: false },
        ],
        customerDeliveryDialog: false,
        selectedTab: null,
        showRelaunchOrderDialog: false,
        workflows: [],
        displayCoherisButton: false,
        coherisUserId: null,
        sendCancelledEmail: true,
        triggersHistoriesData: [],
        cancelReasons: [],
        orderSettings: {},
        forceDeliveredDialog: false,
        dateSelectionDialog: false,
        pickupLocation: undefined,
        pickupLocationSettings: undefined,
        showDisplayPickupCodeDialog: false,
        pickupCode: null,
        pickupCodeError: null,
        loadingPickupCode: false,
        resendProductsDialog: false,
        loadingResendPickupCode: false,
        paymentValidationDialog: false,
        loadingPaymentValidation: false,
        showSubOrderInPPSTooltipFull: [],
      }
    },
    computed: {
      ...mapState(['partnerSettings', 'isDark', 'isOctipasAdmin', 'currentStoreDetails', 'roles', 'isAdmin']),
      isMobile() {
        return this.$vuetify.breakpoint.width < 650
      },
      totalNbProduct: function () {
        return this.getNbProducts(this.order.items)
      },
      orderHeadersComputed() {
        let headers = this.$vuetify.breakpoint.width < 769 ? this.mobileOrderHeaders : this.orderHeaders
        let hasSku = { text: this.$t('product.sku'), value: 'product_sku', sortable: false }
        let hasCurrentStatus = { text: '', value: 'current_status', sortable: false }
        if (this.order.current_status === 'waiting') {
          headers.splice(4, 1)
        }
        for (let item of this.order.items) {
          if (
            item.product_sku &&
            !headers.some((header) => header.value === 'product_sku') &&
            this.$vuetify.breakpoint.width > 700
          ) {
            headers.splice(1, 0, hasSku)
            break
          }
        }
        for (let item of this.order.items) {
          if (item.current_status && !headers.some((header) => header.value === 'current_status')) {
            headers.push(hasCurrentStatus)
            break
          }
        }

        return headers
      },
      subOrderHeadersComputed() {
        return this.$vuetify.breakpoint.width < 769 ? this.mobileSubOrderHeaders : this.subOrderHeaders
      },
      orderHeaders() {
        return [
          { text: this.$t('product.name'), value: 'product_title', sortable: false },
          { text: this.$t('product.barcode'), value: 'product_ean', sortable: false },
          { text: this.$t('product.unitaryPrice'), value: 'product_sale_price', sortable: false },
          { text: this.$t('product.quantity'), value: 'product_quantity', sortable: false },
          { text: this.$t('product.validatedQuantity'), value: 'validated_quantity', sortable: false },
          { text: this.$t('product.returnedQuantity'), value: 'returned_quantity', sortable: false },
          { text: this.$t('product.custom.title'), value: 'custom', sortable: false },
        ]
      },
      mobileOrderHeaders() {
        return [
          { text: this.$t('product.name'), value: 'product_title', sortable: false },
          { text: this.$t('product.unitaryPrice'), value: 'product_sale_price', sortable: false },
          { text: this.$t('product.quantity'), value: 'product_quantity', sortable: false },
          { text: this.$t('product.validatedQuantity'), value: 'validated_quantity', sortable: false },
          { text: this.$t('product.returnedQuantity'), value: 'returned_quantity', sortable: false },
          { text: this.$t('product.custom.title'), value: 'custom', sortable: false },
        ]
      },
      subOrderHeaders() {
        return [
          { text: this.$t('subOrder.numPreparation'), value: 'order_id', sortable: false },
          { text: this.$t('order.type'), value: 'type', sortable: false },
          { text: this.$t('order.status'), value: 'current_status', sortable: false },
          { text: this.$t('order.location'), value: 'source_location_id', sortable: false },
          { text: this.$t('order.products'), value: 'products', sortable: false },
          { text: this.$t('product.validatedProducts'), value: 'validated_products', sortable: false },
          { text: '', value: 'status', sortable: false },
          { text: '', value: 'details', sortable: false },
        ]
      },
      mobileSubOrderHeaders() {
        return [
          { text: this.$t('order.numOrder'), value: 'order_id', sortable: false },
          { text: this.$t('order.type'), value: 'type', sortable: false },
          { text: this.$t('order.status'), value: 'current_status', sortable: false },
          // { text: this.$t("order.location"), value: "source_location_id", sortable: false },
          { text: '', value: 'status', sortable: false },
          { text: '', value: 'details', sortable: false },
        ]
      },
      paymentHeaders() {
        return [
          {
            text: this.$t('payment.reference'),
            value: 'payment_reference',
          },
          {
            text: this.$t('payment.payment_amount'),
            value: 'payment_amount',
          },
          {
            text: this.$t('payment.erp_payment_method_id'),
            value: 'erp_payment_method_id',
          },
          {
            text: this.$t('payment.payment_method_id'),
            value: 'payment_method_id',
          },
        ]
      },
      shipmentHeaders() {
        let shipmentHeadersValues = [
          {
            text: this.$t('shipment.subOrderNumber'),
            value: 'sub_order_id',
          },
          {
            text: this.$t('shipment.externalReference'),
            value: 'external_reference',
          },
          {
            text: this.$t('shipment.trackingReference'),
            value: 'tracking_reference',
          },
        ]
        if (this.subOrderShipments.some((shipment) => shipment.tracking_url)) {
          shipmentHeadersValues.push({ text: '', value: 'action' })
        }
        return shipmentHeadersValues
      },
      customFieldsHeaders() {
        return [
          {
            text: this.$t('order.customFieldName'),
            value: 'name',
          },
          {
            text: this.$t('order.customFieldValue'),
            value: 'value',
          },
        ]
      },
      subOrderTypeTranslations() {
        return [
          { text: this.$t('subOrder.shipFromWarehouse'), value: 'ship_from_warehouse' },
          { text: this.$t('subOrder.clickAndCollect'), value: 'click_and_collect' },
          { text: this.$t('subOrder.inStorePickup'), value: 'in_store_pickup' },
          { text: this.$t('subOrder.shipFromStore'), value: 'ship_from_store' },
          { text: this.$t('subOrder.clickAndReserve'), value: 'click_and_reserve' },
          { text: this.$t('subOrder.interStore'), value: 'inter_store' },
        ]
      },
      itemStatus() {
        return [
          { text: this.$t('product.validated'), value: 'validated' },
          { text: this.$t('product.partiallyValidated'), value: 'partially_validated' },
          { text: this.$t('product.cancelled'), value: 'cancelled' },
        ]
      },
      status() {
        return [
          { text: this.$t('order.waiting'), value: 'waiting' },
          { text: this.$t('order.waitingPayment'), value: 'waiting_payment' },
          { text: this.$t('order.confirmed'), value: 'confirmed' },
          { text: this.$t('order.toPickup'), value: 'to_pickup' },
          { text: this.$t('order.waitingCarrier'), value: 'waiting_carrier' },
          { text: this.$t('order.shippingInProgress'), value: 'shipping_in_progress' },
          { text: this.$t('order.partialShippingInProgress'), value: 'partial_shipping_in_progress' },
          { text: this.$t('subOrder.waitingAllocation'), value: 'waiting_allocation' },
          { text: this.$t('order.delivered'), value: 'delivered' },
          { text: this.$t('order.deliveredPartially'), value: 'partially_delivered' },
          { text: this.$t('order.cancelled'), value: 'cancelled' },
          { text: this.$t('order.blocked'), value: 'blocked' },
          { text: this.$t('return.waitingProductsReceipt'), value: 'waiting_products_receipt' },
          { text: this.$t('return.productsReceived'), value: 'products_received' },
          { text: this.$t('return.finished'), value: 'finished' },
        ]
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('order.itemsPerPage'),
        }
      },
      allowRelaunch() {
        const userIsAllowed =
          this.orderSettings?.display?.display_retry_blocked_orders_button?.includes(this.roles[0]) ||
          this.isOctipasAdmin
        const validRelaunchReasons = ['workflow_not_activated_or_not_found', 'service_not_active']
        const relaunchConditionsFulfilled =
          this.order.current_status === 'blocked' &&
          validRelaunchReasons.includes(this.order.status_history[this.order.status_history.length - 1].status_comment)
        return userIsAllowed && relaunchConditionsFulfilled
      },
      mustDisplayCustomerInformation() {
        let displayCustomerInformation = true
        if (
          !this.order.billing_address &&
          !this.order.shipping_address &&
          !this.order.customer.email &&
          !this.order.customer.last_name &&
          !this.order.customer.first_name &&
          !this.order.customer.phone &&
          !this.order.customer.last_four_digit_phone_number
        ) {
          displayCustomerInformation = false
        }
        return displayCustomerInformation
      },
      subOrderShipments() {
        let shipments = []
        for (let subOrder of this.order.sub_orders) {
          for (let shipment of subOrder.shipments) {
            shipments.push({
              sub_order_id: subOrder.order_id,
              external_reference: shipment.external_reference,
              tracking_reference: shipment.tracking_reference,
              tracking_url: shipment.tracking_url,
            })
          }
        }
        return shipments
      },
      triggerHeaders() {
        return [
          {
            text: this.$t('triggersHistories.reference'),
            value: '_id',
            filterType: 'text',
          },
          {
            text: this.$t('triggersHistories.name'),
            value: 'trigger.trigger_name',
            filterType: 'text',
          },
          {
            text: this.$t('triggersHistories.sourceEntity'),
            value: 'source_entity.entity_id',
            filterType: 'text',
          },
          {
            text: this.$t('triggersHistories.date'),
            value: 'date',
            filterType: 'date',
          },
          {
            text: this.$t('triggersHistories.status'),
            value: 'status',
            filterType: 'array',
            clearable: true,
            arrayValues: this.status,
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          },
          {
            text: '',
            value: 'data-table-expand',
            sortable: false,
          },
        ]
      },
      triggerStatus() {
        return [
          { text: this.$t('triggersHistories.processing'), value: 'processing' },
          { text: this.$t('triggersHistories.success'), value: 'success' },
          { text: this.$t('triggersHistories.error'), value: 'error' },
          { text: this.$t('triggersHistories.relaunched'), value: 'relaunched' },
          { text: this.$t('triggersHistories.relaunchFailed'), value: 'relaunch_failed' },
        ]
      },
      canForceDeliveredStatus() {
        const authorizedStatus = ['partial_shipping_in_progress', 'shipping_in_progress'].includes(
          this.order.current_status
        )
        const authorizedCarrier = ['home_delivery', 'relay_point_delivery'].includes(this.order.carrier?.carrier_type)
        return (
          authorizedStatus &&
          authorizedCarrier &&
          (this.isOctipasAdmin || this.orderSettings.display?.display_force_delivered_button.includes(this.roles[0]))
        )
      },
      pickupDeadline() {
        return this.order?.pickup_deadline
      },
      maxExtensionDate() {
        return computeMaxExtensionDate(this.order, this.pickupLocationSettings, this.pickupLocation)
      },
      displayModifyPickupDeadlineButton() {
        return canDisplayPickupDeadlineButton(this.pickupLocationSettings, this.isOctipasAdmin, this.roles, this.order)
      },
      maxExtensionDateReached() {
        return maxExtensionDateReached(this.maxExtensionDate, this.order.pickup_deadline.split('T')[0])
      },
      displayCodeIntroSentence() {
        return this.loadingPickupCode || this.pickupCodeError ? '' : this.$t('order.displayPickupCodeMessage')
      },
      allowedPaymentMethods() {
        const methods = this.orderSettings?.payments?.payment_methods || []
        return methods.filter(
          (pm) => pm.activate && (this.isOctipasAdmin || pm.allowed_roles?.some((role) => this.roles.includes(role)))
        )
      },
      canValidatePayments() {
        if (!this.order) return false
        return (
          this.order.current_status === 'waiting_payment' &&
          this.order.payment_validation_required === true &&
          this.allowedPaymentMethods.length > 0
        )
      },
      pickupCodeDisplayAllowed() {
        const codeSettings = this.orderSettings?.delivery?.pickup_code || {}
        const pickupCodeActivated = codeSettings.activate
        const allowedRoles = Array.isArray(codeSettings.display_allowed_roles) ? codeSettings.display_allowed_roles : []
        const isRoleAllowed = this.isOctipasAdmin || allowedRoles.some((role) => this.roles.includes(role))
        return pickupCodeActivated && isRoleAllowed && this.order?.has_pickup_code
      },
      pickupCodeResendAllowed() {
        const codeSettings = this.orderSettings?.delivery?.pickup_code || {}
        const pickupCodeActivated = codeSettings.activate
        const allowedRoles = Array.isArray(codeSettings.resend_allowed_roles) ? codeSettings.resend_allowed_roles : []
        const isRoleAllowed = this.isOctipasAdmin || allowedRoles.some((role) => this.roles.includes(role))
        return pickupCodeActivated && isRoleAllowed && this.order?.has_pickup_code
      },
    },
    async mounted() {
      this.loadingBtn = true
      this.loadingData = true
      this.refundService = new RefundService(this.$auth)
      this.documentService = new DocumentService(this.$auth)
      if (this.$route.params.id) {
        await this.initializeVue()
        await this.getSettingsByCountry(this.currentStoreDetails?.address?.country_id)
      }

      if (this.$store.state.currentTab) {
        this.selectedTab = this.$store.state.currentTab
      }

      // Check if we need to display coheris button
      if (this.$store.state.partnerSettings.coheris_access?.enabled) {
        this.getCoherisUserId().then((coherisUserId) => {
          this.displayCoherisButton = !!coherisUserId
          this.coherisUserId = coherisUserId
        })
      }
      this.loadingBtn = false
      this.loadingData = false
    },
    methods: {
      async initializeVue() {
        await this.get()

        await applyPickupLocationToComponent(this)

        if (this.currentStoreDetails?.address?.country_id) {
          this.orderSettings = await OrderSettingsService.get(
            this.$auth.accessToken,
            this.currentStoreDetails.address.country_id
          )
          this.setCancelReasons(this.orderSettings)
        }
      },
      safeURL(url) {
        return sanitizeUrl(url)
      },
      getCoherisUserId: async function () {
        const promise = new Promise((resolve) => {
          // No response in 100ms -> not in an iframe
          const timeout = setTimeout(() => {
            resolve(null)
          }, 100)

          // We got an answer
          window.addEventListener('message', (event) => {
            if (event.data.coherisUserId) {
              // We are in a Coheris panel
              clearTimeout(timeout)
              resolve(event.data.coherisUserId)
            }
          })
        })

        // Ask parent window the display state
        window.parent.postMessage('getCoherisUserId', '*')

        return promise
      },
      openCoherisCase: function () {
        const xhr = new XMLHttpRequest()
        xhr.open(
          'GET',
          `${this.$store.state.partnerSettings.coheris_access?.url}/external-events-connector/rest/pushmaster/1.0/exec/${this.coherisUserId}?action=rhino&email=${this.order.customer.email}&origin=oms&orderId=${this.$route.params.id}`
        )
        xhr.send()
      },
      openHistoryDialog: function (item) {
        this.showHistoryDialog = true
        this.targetItem = item
      },
      closeHistoryDialog: function () {
        this.showHistoryDialog = false
      },
      getNbProducts: function (orderItems) {
        let totalNbProduct = 0
        for (const productLine of orderItems) {
          totalNbProduct += productLine.product_quantity
        }
        return totalNbProduct
      },
      getNbProductsValidated: function (orderItems) {
        let nbProduct = 0
        for (let productLine of orderItems) {
          if (productLine.validated_quantity) {
            nbProduct += productLine.validated_quantity
          }
        }
        return nbProduct
      },
      getIndexOfSuborder(item) {
        let index = this.order.sub_orders
          .map(function (subOrder) {
            return subOrder.order_id
          })
          .indexOf(item.order_id)
        return index
      },
      async get() {
        this.loadingData = true
        const path = '/v1/int/orders/' + this.$route.params.id
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        try {
          this.order = await this.$Amplify.API.get('orders', path, myInit)
          if (this.order?.sub_orders?.length) {
            this.listSubOrdersPickPackShips()
          }
          this.loadingBtn = false
          this.loadingData = false
        } catch (e) {
          console.log(e)
          EventBus.$emit('displaySnackbar', {
            display: true,
            color: 'error',
            text: 'Error during order loading',
          })
        } finally {
          this.loadingData = false
        }
      },
      async listDocuments() {
        if (this.documentsLoaded) {
          return
        }
        this.loadingDocuments = true
        this.documentsLoadError = false
        try {
          this.documents = await this.documentService.listByEntity('orders', this.order.order_id)
          this.documentsLoaded = true
        } catch (e) {
          console.log(e)
          this.documents = []
          this.documentsLoadError = true
        } finally {
          this.loadingDocuments = false
        }
      },
      cancelSubOrder: function (cancelReason) {
        this.updateSubOrder(this.indexActionSuborder, 'cancelled', cancelReason)
      },
      updateSubOrder: function (index, newStatus, cancelReason = undefined) {
        this.dialogCancelSubOrder = false
        this.loadingBtn = true
        let sub_order = this.order.sub_orders[index]

        let subOrderCopy = Object.assign({}, sub_order)
        subOrderCopy.current_status = newStatus
        subOrderCopy.cancel_reason = cancelReason
        subOrderCopy.reorchestrate = this.reorchestrate
        let path = '/v1/int/sub-orders/' + subOrderCopy.order_id
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          body: subOrderCopy,
        }
        this.$Amplify.API.put('sub-orders', path, myInit)
          .then((/*res*/) => {
            this.get()
            // this.order.sub_orders[index] = res
            // this.order.sub_orders.push() // needed to update the view
            // this.loadingBtn = false
          })
          .catch((e) => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              color: 'error',
              text: 'Error during updating sub-order',
            })
            console.log(e)
            this.loadingBtn = false
          })
      },
      getStatusTranslation: function (statusValue) {
        let status = this.status.find((status) => status.value == statusValue)
        if (status) {
          return status.text
        }
        return statusValue
      },
      getItemStatusTranslation: function (statusValue) {
        let status = this.itemStatus.find((status) => status.value == statusValue)
        if (status) {
          return status.text
        }
        return statusValue
      },
      getTypeTranslation: function (typeValue) {
        let type = this.subOrderTypeTranslations.find((type) => type.value == typeValue)
        if (type) {
          return type.text
        }
        return typeValue
      },
      createReturn(orderId) {
        this.$router.push({ name: 'returnCreate', params: { id: orderId } })
      },
      orderAlreadyFullyReturned(order) {
        if (['delivered', 'partially_delivered'].includes(this.order.current_status)) {
          for (let item of order.items) {
            let returnedQuantity = item.returned_quantity || 0
            let validatedQuantity = item.validated_quantity || 0
            if (returnedQuantity < validatedQuantity) {
              return false
            }
          }
          return true
        } else {
          return false
        }
      },
      viewDetails(subOrderId) {
        this.$router.push('/sub-orders/' + subOrderId)
      },
      openCustomItemFieldsDialog(item) {
        this.showCustomItemFieldsDialog = true
        this.customItems = item.custom
      },
      closeCustomFieldsDialog() {
        this.showCustomItemFieldsDialog = false
      },
      cancel: function (cancelReason) {
        this.patch('cancelled', cancelReason)
      },
      async patch(newStatus, cancelReason = undefined, workflowId = undefined) {
        if (newStatus === 'cancelled') {
          this.cancellation = true
        } else {
          this.loadingBtn = true
          this.loadingData = true
        }

        let patchData = {
          current_status: newStatus,
          cancel_reason: cancelReason,
        }

        if (workflowId) {
          patchData.workflow_id = workflowId
        }
        if (Array.isArray(this.order.custom) && this.order.custom.length) {
          patchData.custom = this.order.custom
        }
        if (Array.isArray(this.order.payments) && this.order.payments.length) {
          patchData.payments = this.order.payments
        }

        patchData.send_cancelled_email = this.sendCancelledEmail

        const patchedOrder = await OrderService.patch(this.$auth.accessToken, this.$route.params.id, patchData)
        if (patchedOrder) {
          this.order = patchedOrder
        }

        this.loadingBtn = false
        this.cancellation = false
        this.loadingData = false
      },
      async listReturns() {
        if (this.returnsLoaded) {
          return
        }
        this.loadingData = true

        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            order_id: this.$route.params.id,
          },
        }
        try {
          const res = await this.$Amplify.API.get('returns', '/v1/int/returns', myInit)
          this.returns = res.data
          // Flagged only once the returns are in: a failed request must stay retryable.
          this.returnsLoaded = true
        } catch (e) {
          console.log(e)
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('return.returnsLoadingError'),
            color: 'error',
          })
        } finally {
          this.loadingData = false
        }
      },
      getNbProductsReturned: function (returnItems) {
        let nbProduct = 0
        for (let productLine of returnItems) {
          if (productLine.returned_quantity) {
            nbProduct += productLine.returned_quantity
          } else if (productLine.return_quantity) {
            nbProduct += productLine.return_quantity
          }
        }
        return nbProduct
      },
      viewReturnDetails(subOrderId) {
        this.$router.push('/returns/' + subOrderId)
      },
      async listRefunds() {
        if (this.refundsLoaded) {
          return
        }
        this.loadingData = true
        this.refundsLoadError = false
        try {
          const res = await this.refundService.list({}, [], { order_id: this.order.order_id }, false)
          // res is undefined only on a failed fetch → show an error state, not a false "no refunds".
          if (res) {
            this.refunds = res.data || []
            this.refundsLoaded = true
          } else {
            this.refundsLoadError = true
          }
        } finally {
          this.loadingData = false
        }
      },
      openCustomerDeliveryModalOrUpdate: function () {
        if (this.order.type === 'click_and_reserve') {
          this.customerDeliveryDialog = true
        } else {
          this.patch('delivered')
        }
      },
      validateCustomerDelivery: function () {
        this.patch('delivered')
        this.customerDeliveryDialog = false
      },
      openOrderRelaunchDialog: function () {
        this.loadingData = true
        this.showRelaunchOrderDialog = true
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            page: 1,
            itemsPerPage: 50,
          },
        }
        this.$Amplify.API.get('order-workflows', '/v1/int/order-workflows', myInit)
          .then((res) => {
            this.workflows = res.data.filter((workflow) => workflow.activated)
            this.loadingData = false
          })
          .catch((e) => {
            this.loadingData = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('orderWorkflow.errorLoadingWorkflows'),
              color: 'error',
            })
          })
      },
      relaunchOrder(newWorkflowId) {
        const relaunchWorkflowId = newWorkflowId === false ? this.order.workflow_id : newWorkflowId
        this.patch('waiting', undefined, relaunchWorkflowId)
      },
      getSettingsByCountry(countryIso3) {
        // get the settings of the country selected
        this.loadingBtn = true
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.get('settings', '/v1/settings/' + countryIso3, myInit)
          .then((res) => {
            this.setCancelReasons(res)
            this.orderSettings = res
            this.loadingBtn = false
          })
          .catch((e) => {
            if (e.response?.status === 404 && countryIso3 !== 'FRA') {
              console.log(`No settings for country ${this.currentStoreDetails?.address?.country_id}`)
              this.getSettingsByCountry('FRA')
            }
            this.loadingBtn = false
          })
      },
      copyReference: function (valueToCopy) {
        navigator.clipboard.writeText(valueToCopy).then(
          () => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('orderWorkflow.copySuccessful'),
              color: 'success',
            })
          },
          () => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('orderWorkflow.copyFailed'),
              color: 'error',
            })
          }
        )
      },
      getTriggersStatusTranslation: function (statusValue) {
        const status = this.triggerStatus.find((status) => status.value == statusValue)
        if (status) {
          return status.text
        } else {
          return statusValue
        }
      },
      getTriggerStatusBackgroundColor,
      getTriggerStatusTextColor,
      async listTriggerHistories() {
        // The returns are otherwise only fetched when the returns tab is opened, and their ids are
        // needed to list their histories here.
        if (this.partnerSettings.returns_access) {
          await this.listReturns()
        }
        // Same for the refunds, fetched on demand by the refunds tab. Both loaders must run before
        // loadingData is raised: their own `finally` / `catch` lowers it.
        await this.listRefunds()

        this.loadingData = true

        // Sub order and return ids embed the parent order id, so neither a substring search (which
        // also catches unrelated orders sharing the same leading characters) nor an exact match
        // (which drops them) can list the histories of this order. Send the ids explicitly.
        const subOrdersIds = this.order.sub_orders.map((subOrder) => subOrder.order_id)
        const returnIds = this.returns.map((productsReturn) => productsReturn.return_id)
        // Only the refunds attached to this order and to no return: the API never derives order_id
        // from return_id nor checks one against the other, so a refund carrying both may reference a
        // return belonging to another order. The exact order_id comparison is needed as well,
        // listRefunds() matching order_id partially.
        const refundIds = this.refunds
          .filter((refund) => refund.order_id === this.order.order_id && !refund.return_id)
          .map((refund) => refund.refund_id)
        const entityIds = [...new Set([this.order.order_id, ...subOrdersIds, ...returnIds, ...refundIds])].filter(
          Boolean
        )

        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            items_per_page: 50,
            sort_by: 'date',
            sort_desc: true,
            'source_entity.entity_id': entityIds,
            exact_entity_id: true,
          },
        }

        this.$Amplify.API.get('triggers', '/v1/int/triggers-histories', myInit)
          .then((res) => {
            this.triggersHistoriesData = res.data
            this.loadingData = false
          })
          .catch((e) => {
            this.loadingData = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('triggersHistories.errorTriggersHistoriesLoading'),
              color: 'error',
            })
          })
      },
      goToTriggerHistory: function (id) {
        this.$router.push({ path: `/triggers-histories/${id}` })
      },
      setCancelReasons(settings) {
        const cancelReasonsTranslations = [
          { text: this.$t('subOrder.clientChangeOfMind'), value: 'client_changed_his_mind' },
          { text: this.$t('subOrder.deterioratedProducts'), value: 'deteriorated_products' },
          { text: this.$t('subOrder.orderedMultipleTimes'), value: 'ordered_multiple_times' },
          { text: this.$t('subOrder.productsNotAvailable'), value: 'products_not_available' },
          { text: this.$t('subOrder.customerDidNotCollect'), value: 'customer_did_not_collect_order' },
        ]
        if (settings?.display) {
          if (settings.display.order_cancel_reasons) {
            this.cancelReasons = []
            for (const orderCancelReason of settings.display.order_cancel_reasons) {
              const translatedCancelReason = cancelReasonsTranslations.find((translatedCancelReason) => {
                return orderCancelReason === translatedCancelReason.value
              })
              if (translatedCancelReason) {
                this.cancelReasons.push(translatedCancelReason)
              } else {
                this.cancelReasons.push({ text: orderCancelReason, value: orderCancelReason })
              }
            }
          } else {
            this.cancelReasons = cancelReasonsTranslations
          }
          if (settings.display.display_custom_cancel_reason) {
            this.cancelReasons.push({ text: this.$t('subOrder.otherReason'), value: 'other' })
          }
        } else {
          this.cancelReasons = cancelReasonsTranslations
        }
      },
      validateForceDelivered() {
        this.patch('delivered')
        this.forceDeliveredDialog = false
      },
      async validatePayments(payments) {
        this.loadingPaymentValidation = true
        try {
          const patchData = {
            current_status: 'waiting',
            payments,
          }
          const patchedOrder = await OrderService.patch(this.$auth.accessToken, this.$route.params.id, patchData)
          if (patchedOrder) {
            this.order = patchedOrder
            this.paymentValidationDialog = false
          }
        } finally {
          this.loadingPaymentValidation = false
        }
      },
      async onDeadlineChange(newDeadline) {
        this.loadingBtn = true
        const patchedOrder = await updatePickupDeadline(this.$auth.accessToken, this.order.order_id, newDeadline)
        if (patchedOrder) {
          this.order = patchedOrder
        }
        this.loadingBtn = false
      },
      deadlinesAreValid(checkedDeadlineDate) {
        return checkDeadlineIsValid(checkedDeadlineDate, this.pickupLocation, this.order.pickup_deadline)
      },
      getPickupCodeErrorMessage(error) {
        const message = error.response?.data?.message

        const errorMap = {
          user_not_authorized: this.$t('order.pickupCodeUnauthorized'),
          pickup_code_disabled: this.$t('order.pickupCodeFeatureDisabled'),
          pickup_code_not_found: this.$t('order.pickupCodeNotYetGenerated'),
          wrong_order_type: this.$t('order.pickupCodeNotApplicable'),
          order_not_found: this.$t('order.notFound'),
          pickup_location_not_found: this.$t('order.pickupLocationNotFound'),
        }

        return errorMap[message] || this.$t('order.errorLoadingPickupCode')
      },
      async displayPickupCodeDialog() {
        this.showDisplayPickupCodeDialog = true
        // we don't fetch already successfully fetched pickup code
        if (!this.pickupCode) {
          await this.getPickupCode()
        }
      },
      async getPickupCode() {
        this.loadingPickupCode = true
        this.pickupCodeError = null
        try {
          this.pickupCode = await OrderService.getPickupCode(this.$auth.accessToken, this.$route.params.id)
        } catch (error) {
          console.error('Error fetching pickup code:', error)
          this.pickupCode = null
          this.pickupCodeError = error.response?.data?.message || 'unknown_error'

          EventBus.$emit('displaySnackbar', {
            display: true,
            color: 'error',
            text: this.getPickupCodeErrorMessage(error),
          })
        } finally {
          this.loadingPickupCode = false
        }
      },
      async resendPickupCode() {
        this.loadingResendPickupCode = true
        try {
          await OrderService.resendPickupCode(this.$auth.accessToken, this.$route.params.id)
          this.resendProductsDialog = false
        } catch (error) {
          console.error('Error resending pickup code:', error)

          EventBus.$emit('displaySnackbar', {
            display: true,
            color: 'error',
            text: this.getPickupCodeErrorMessage(error),
          })
        } finally {
          this.loadingResendPickupCode = false
        }
      },
      listSubOrdersPickPackShips() {
        let { itemsPerPage } = this.options
        const subOrdersIds = this.order.sub_orders.map((subOrder) => subOrder.order_id)
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            page: 1,
            itemsPerPage: itemsPerPage,
            location_id: this.isCustomerService ? undefined : [this.currentStore],
            status: ['picking', 'packing'],
            sub_orders_id: [subOrdersIds],
          },
        }
        this.$Amplify.API.get('pick-pack-ships', '/v1/int/pick-pack-ships', myInit)
          .then((res) => {
            if (Array.isArray(res.data) && res.data.length) {
              for (const pickPackShip of res.data) {
                for (let pickPackShipSubOrder of pickPackShip.sub_orders) {
                  let matchingSubOrder = this.order.sub_orders.find(
                    (subOrder) => subOrder.order_id === pickPackShipSubOrder.order_id
                  )
                  if (matchingSubOrder) {
                    this.$set(matchingSubOrder, 'pick_pack_ship', {
                      reference: pickPackShip._id,
                      picker: pickPackShip.picker,
                    })
                  }
                }
              }
            }
          })
          .catch((e) => {
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('subOrder.errorLoadingPickPackShips'),
              color: 'error',
            })
          })
      },
      togglePickPackShipTooltipFull(index) {
        this.$set(this.showSubOrderInPPSTooltipFull, index, !this.showSubOrderInPPSTooltipFull[index])
        setTimeout(() => {
          this.$set(this.showSubOrderInPPSTooltipFull, index, false)
        }, 3000)
      },
      getPickPackShipMessage(pickPackShipPicker, pickPackShipReference) {
        const hasPicker = pickPackShipPicker
        const translationKey = hasPicker
          ? 'subOrder.alreadyInPickPackShipTable'
          : 'subOrder.alreadyInPickPackShipTableNoPicker'
        const replacingParameters = { picker: hasPicker || undefined, reference: pickPackShipReference }
        return this.$t(translationKey, replacingParameters)
      },
    },
    watch: {
      selectedTab: function (newVal) {
        this.$store.commit('setCurrentTab', newVal)
      },
      $route: async function () {
        // When route is updated -> refresh the order
        if (this.$route.params.id !== this.order?.order_id) {
          await this.initializeVue()
          this.getSettingsByCountry(this.currentStoreDetails?.address?.country_id)
        }
      },
    },
  }
</script>
<style scoped>
  .v-btn-footer {
    max-width: 97%;
  }

  button >>> .v-btn__content {
    width: 100%;
    white-space: normal;
  }

  .not-underlined-link {
    text-decoration: none;
  }

  .aligned-icon {
    margin-bottom: 1px;
  }

  ::v-deep tr.v-data-table__expanded__content {
    box-shadow: none !important;
  }

  ::v-deep .v-data-table--mobile tr.v-data-table__expanded__content > td {
    display: block;
    width: 100%;
    height: auto;
  }
</style>
